import type { Package } from '@mypoz/shared';

import { ORDERS_ROUTE, PACKAGES_ROUTE } from '@/shell/navigation.js';

import { getNotificationDestination } from './catalog.js';
import type { DashboardNotification } from './types.js';

/**
 * Where selecting this specific notification should take the user, or `null` when there's nowhere
 * to go — the caller's job at that point is to open it in place instead (see
 * `NotificationThreadPanel`).
 *
 * PACKAGE and ORDER are resolved per-notification rather than per-category, unlike every other
 * type, which still just asks `getNotificationDestination` for its one fixed landing page.
 *
 * PACKAGE matches by internal package id, not tracking number. MYP-66 fixtured `tid` as the
 * tracking number, guessing at what a "packages msg" notification would plausibly carry; MYP-112
 * confirmed live that it is actually the package's own internal `id` — the same value
 * `package_list` sends as `id`/`pkg_id` and `Packages.tsx` matches its own `?package=` query
 * param against. Matching on `trackingNumber` (the old code) would never have found a package,
 * silently sending every PACKAGE notification down the in-place fallback below instead of its
 * real thread — this fixes that rather than carrying the wrong assumption forward now that it's
 * disproven.
 *
 * The packages to search are passed in rather than imported (MYP-40): they are fetched now, not a
 * module constant, so this function cannot reach them on its own and the caller — already a
 * component with `usePackages()` in hand — supplies them. An empty list simply means the fetch has
 * not landed yet, which resolves to `null` and the same in-place fallback below.
 *
 * That lookup can still fail even with the right field — a stale notification, or a package that
 * has since been deleted. Returning `null` in that case rather than a dead link is deliberate: the
 * fallback is the same in-place thread panel every notification used to open before this ticket,
 * not a broken navigation.
 *
 * ORDER links from `orderno`, not `tid`. MYP-66 fixtured ORDER's `tid` as the real order number on
 * the same "no separate internal id" reasoning that's true of `SubmittedOrder` in this app — but
 * MYP-112 confirmed live that `get_msgs` itself does keep the two separate: `tid` is the order's
 * internal vendor id (what `order_list`'s own `orderid` parameter expects), and `orderno` is the
 * human order number `Orders.tsx` actually matches its `?order=` query param against
 * (`order.orderNumber === targetOrderNumber`). Linking from `tid` would build a URL nothing on the
 * Orders page ever matches — silently degrading every single ORDER notification to "open the list,
 * expand nothing," which is indistinguishable from a stale reference and just as wrong for a fresh
 * one. `orderno` is what MYP-66 meant to use and named the wrong field for.
 *
 * A missing `orderno` (not expected on a real ORDER row, but not a case to crash over either)
 * resolves to `null` the same way an unresolved PACKAGE does — there's no in-place thread panel
 * built for ORDER's content specifically, so this degrades to the same general fallback panel
 * every other unresolved notification already opens.
 */
export function resolveNotificationLink(
  notification: DashboardNotification,
  packages: readonly Package[],
): string | null {
  if (notification.type === 'PACKAGE') {
    const pkg = packages.find((item) => item.id === notification.tid);
    return pkg ? `${PACKAGES_ROUTE}?panel=messages&package=${pkg.id}` : null;
  }

  if (notification.type === 'ORDER') {
    return notification.orderno
      ? `${ORDERS_ROUTE}?order=${encodeURIComponent(notification.orderno)}`
      : null;
  }

  return getNotificationDestination(notification.type);
}
